'use client'

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Keeps the `<html lang>` attribute in sync with the active i18n language.
 *
 * The root layout (Server Component) renders a static `lang="fr"` (the i18n
 * `fallbackLng`). Once the client resolves the user's real language
 * (localStorage / cookie / navigator), and on every manual language switch,
 * we update the attribute so assistive technologies announce content with the
 * correct pronunciation (WCAG 3.1.1 — Language of Page). i18next emits
 * `languageChanged` on every switch.
 *
 * Renders nothing; mounted once inside the i18n provider tree.
 */
export const HtmlLangSync = () => {
  const { i18n } = useTranslation()

  useEffect(() => {
    const apply = (lng: string) => {
      // `languageOnly` load → strip any region suffix (e.g. `fr-FR` → `fr`).
      document.documentElement.lang = (lng || 'fr').split('-')[0]
    }

    apply(i18n.language)
    i18n.on('languageChanged', apply)
    return () => {
      i18n.off('languageChanged', apply)
    }
  }, [i18n])

  return null
}
